/* Partes comuns da Planilha padrão e da Planilha personalizada: leitura do .xlsx no
   navegador, modelo da categoria, envio para POST /lancamentos/excel e resultado. */

import { api, detalhesErro, msgErro } from '../../core/api';
import type { Planilha } from '../../core/analisePlanilha';
import { carregarXlsx } from '../../core/arquivos';
import { isApi } from '../../core/config';
import { cat, type ResultadoTela } from '../../core/estado';
import { esc, num, sleep } from '../../core/formato';
import { importarPlanilhaDemo } from '../../demo/api';
import type { Campo, Categoria, ResultadoImportacao } from '../../tipos/api';
import { escLabel } from '../../ui/componentes';
import { ic } from '../../ui/dom';

export type { ResultadoTela };

export const MAX_BYTES = 20 * 1024 * 1024;

const EXEMPLOS: Record<string, string | number> = {
  descricao: 'Medição de março', cnpj_fornecedor: '11.482.310/0001-77', consumo: 2180, consumo_mensal: 2180,
  existentes_recarga: 12, ano_veiculo: 2020, quantidade: 42, valor_total: 18500, volume_m3: 320,
};

/** Valor de exemplo de cada campo, usado no modelo .xlsx e nas dicas de valor padrão. */
export function exemploCampo(x: Campo): string | number | Date {
  if (x.nome === 'data') return new Date('2026-03-02T12:00:00');
  if (x.tipo === 'obra') return cat().obras[0]?.nome || '';
  if (x.tipo.startsWith('lista:')) {
    const itens = cat().listas[x.tipo.slice(6)] || [];
    return (itens.find((i) => String(i.id) === String(x.padrao)) || itens[0])?.nome || '';
  }
  if (x.tipo === 'opcoes') return x.padrao ?? x.opcoes[0];
  if (x.padrao !== null && x.padrao !== undefined) return x.padrao;
  return EXEMPLOS[x.nome] ?? '';
}

/** Lê a primeira aba; cabeçalho = linha mais preenchida entre as 15 primeiras (pula títulos acima da tabela). */
export async function lerPlanilha(file: File): Promise<Planilha> {
  if (!/\.xls[xm]$/i.test(file.name)) throw new Error('Envie um arquivo .xlsx ou .xlsm.');
  if (file.size > MAX_BYTES) throw new Error('A planilha passa de 20 MB.');
  const XLSX = await carregarXlsx();
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const aba = wb.SheetNames[0];
  const linhas = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[aba], { header: 1, raw: true, defval: null });
  const preenchidas = (l: unknown[] | undefined) => (l || []).filter((v) => v !== null && String(v).trim() !== '').length;
  let iCab = -1;
  linhas.slice(0, 15).forEach((l, i) => { if (preenchidas(l) > (iCab < 0 ? 0 : preenchidas(linhas[iCab]))) iCab = i; });
  if (iCab < 0) throw new Error('A planilha está vazia.');
  const rows = linhas.slice(iCab + 1).map((l, i) => ({ l, n: iCab + 2 + i })).filter((r) => preenchidas(r.l) > 0);
  if (!rows.length) throw new Error('A planilha não tem linhas de dados abaixo do cabeçalho.');
  return {
    arquivo: file.name, aba, linhaCab: iCab + 1, rows,
    cabecalho: linhas[iCab].map((h) => (h === null ? '' : String(h).trim())),
  };
}

/** Modelo .xlsx da categoria: aba de lançamentos com exemplo, instruções e uma aba por lista do catálogo. */
export async function baixarModelo(c: Categoria): Promise<void> {
  const XLSX = await carregarXlsx();
  const ws = XLSX.utils.aoa_to_sheet([c.campos.map((x) => x.rotulo), c.campos.map(exemploCampo)], { cellDates: true });
  ws['!cols'] = c.campos.map((x) => ({ wch: Math.max(16, x.rotulo.length + 2) }));
  const opcoesDe = (x: Campo) => x.tipo === 'obra' ? cat().obras.map((o) => o.nome).join('; ')
    : x.tipo.startsWith('lista:') ? `Nome como na aba ${x.tipo.slice(6)} (ou uma descrição parecida)` : x.tipo === 'opcoes' ? x.opcoes.join(', ') : '';
  const comoPreencher = (x: Campo) => [x.dica, opcoesDe(x), x.padrao !== null && x.padrao !== undefined ? `Em branco: ${x.padrao}` : '']
    .filter(Boolean).join(' — ') || (x.tipo === 'numero' ? 'Número' : x.tipo === 'data' ? 'Data' : 'Texto');
  const inst = XLSX.utils.aoa_to_sheet([
    [`Modelo — ${c.nome} (${escLabel(c.escopo)})`], [c.descricao], [],
    ['Coluna', 'Obrigatória', 'Como preencher'],
    ...c.campos.map((x) => [x.rotulo, x.obrigatorio ? 'Sim' : 'Não', comoPreencher(x)]),
    [], ['Uma linha por lançamento. Linhas totalmente vazias são ignoradas. Não renomeie as colunas.'],
  ]);
  inst['!cols'] = [{ wch: 36 }, { wch: 12 }, { wch: 90 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Lançamentos');
  XLSX.utils.book_append_sheet(wb, inst, 'Instruções');
  [...new Set(c.campos.filter((x) => x.tipo.startsWith('lista:')).map((x) => x.tipo.slice(6)))].forEach((nome) =>
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['ID', 'Nome'], ...(cat().listas[nome] || []).map((i) => [i.id, i.nome])]), nome.slice(0, 31)));
  XLSX.writeFile(wb, `modelo_${c.slug}.xlsx`);
}

export interface Envio {
  categoria: Categoria;
  /** Nome que aparece em "Meus envios". */
  arquivo: string;
  /** Arquivo enviado ao backend e o mapeamento campo → coluna dele. */
  file: File | Blob;
  mapa: Record<string, string>;
  fixos: Record<string, string>;
  aba?: string;
  linhaCab?: number;
  /** Linhas já com os valores por campo (modo demonstração). */
  linhas: { dados: Record<string, unknown>; n: number }[];
  /** Linha do arquivo enviado → linha da planilha original (quando o arquivo é gerado). */
  linhaOriginal?: (linha: number) => number;
}

/** Envia e devolve o resultado para a tela (sucesso ou onde parou). */
export async function enviarImportacao(e: Envio): Promise<ResultadoTela> {
  try {
    let res: ResultadoImportacao;
    if (isApi()) {
      const fd = new FormData();
      fd.append('arquivo', e.file, e.arquivo);
      fd.append('categoria', e.categoria.slug);
      fd.append('mapeamento', JSON.stringify(e.mapa));
      fd.append('campos_fixos', JSON.stringify(e.fixos));
      if (e.aba) fd.append('aba', e.aba);
      if (e.linhaCab) fd.append('linha_cabecalho', String(e.linhaCab));
      res = await api<ResultadoImportacao>('POST', '/lancamentos/excel', { form: fd });
    } else {
      await sleep(400);
      res = importarPlanilhaDemo(e.categoria.slug, e.arquivo, e.linhas.map((l) => ({ dados: { ...e.fixos, ...l.dados }, n: l.n })));
    }
    return { ok: true, n: num(res.linhas_inseridas), lidas: num(res.linhas_lidas), arquivo: e.arquivo };
  } catch (erro) {
    const d = detalhesErro(erro);
    const interno = d.detalhes ? msgErro(d.detalhes) : '';
    const linha = d.linha_excel && isApi() && e.linhaOriginal ? e.linhaOriginal(Number(d.linha_excel)) : d.linha_excel;
    return {
      ok: false, msg: interno || (erro as Error).message, linha,
      parcial: Boolean(d.importacao_parcial), inseridas: num(d.linhas_inseridas),
    };
  }
}

export function resultadoHtml(res: ResultadoTela | null): string {
  if (!res) return '';
  if (res.ok) {
    return `<div class="callout success">${ic('i-checkcircle')}<div><b>${res.n} lançamento(s) importado(s).</b><br>${res.lidas} linha(s) lidas de ${esc(res.arquivo)}. As emissões já entram no painel e na análise.</div></div>`;
  }
  return `<div class="callout warn">${ic('i-alert')}<div><b>A importação parou${res.linha ? ` na linha ${res.linha} do Excel` : ''}.</b><br>${esc(res.msg)}
    ${res.parcial ? `<br>${res.inseridas} linha(s) anteriores já tinham sido gravadas e <b>não foram desfeitas</b>.` : ''}</div></div>`;
}
